import { useTranslation } from 'react-i18next'
import { appStoreUrl, googlePlayStoreUrl } from '@redevicer/shared'
import BrandWordmark from './BrandWordmark'
import { dismissMobileAppOnboarding, mobileWebPlatform } from '../utils/isMobileWebBrowser'

const iosHref =
  (import.meta.env.VITE_APP_STORE_URL as string | undefined)?.trim() || appStoreUrl()
const androidHref =
  (import.meta.env.VITE_GOOGLE_PLAY_URL as string | undefined)?.trim() || googlePlayStoreUrl()

type Props = {
  onDismiss: () => void
}

function IconApple({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" width={18} height={18}>
      <path
        fill="currentColor"
        d="M16.365 12.146c.025 2.637 2.308 3.518 2.337 3.531-.02.064-.365 1.248-1.204 2.474-.726 1.05-1.48 2.098-2.667 2.12-1.168.022-1.545-.694-2.884-.694-1.339 0-1.758.674-2.867.716-1.15.043-2.026-1.156-2.762-2.204-1.49-2.155-2.627-6.09-1.098-8.748.76-1.32 2.117-2.155 3.593-2.176 1.12-.021 2.177.753 2.867.753.69 0 1.984-.93 3.345-.793.569.024 2.164.23 3.186 1.737-.083.051-1.903 1.112-1.884 3.314zM13.827 3.52c.608-.737 1.018-1.762.905-2.782-.876.035-1.938.584-2.568 1.32-.564.653-1.057 1.697-.923 2.697.977.075 1.975-.496 2.586-1.235z"
      />
    </svg>
  )
}

function IconAndroid({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" width={18} height={18}>
      <path
        fill="currentColor"
        d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 0 0-.83.22l-1.88 3.24a11.463 11.463 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2.617.617 0 0 0-.27.83l1.84 3.18C4.18 11.31 2.5 14.08 2.5 17h19c0-2.92-1.68-5.69-4.9-7.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"
      />
    </svg>
  )
}

export default function MobileAppOnboarding({ onDismiss }: Props) {
  const { t } = useTranslation()
  const platform = mobileWebPlatform()
  const storeHref = platform === 'android' ? androidHref : iosHref
  const StoreIcon = platform === 'android' ? IconAndroid : IconApple
  const storeLabel = platform === 'android' ? t('vc.headerGooglePlay') : t('vc.headerAppStore')

  function close() {
    dismissMobileAppOnboarding()
    onDismiss()
  }

  return (
    <div
      className="mobile-app-splash"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-app-splash-title"
    >
      <div className="mobile-app-splash__backdrop" aria-hidden="true" />
      <div className="mobile-app-splash__panel">
        <div className="mobile-app-splash__accent" aria-hidden="true" />
        <div className="mobile-app-splash__stage" aria-hidden="true">
          <span className="mobile-app-splash__orb mobile-app-splash__orb--left" />
          <span className="mobile-app-splash__orb mobile-app-splash__orb--right" />
          <div className="mobile-app-splash__card mobile-app-splash__card--back" />
          <div className="mobile-app-splash__card mobile-app-splash__card--mid" />
          <div className="mobile-app-splash__phone">
            <div className="mobile-app-splash__phone-notch" />
            <div className="mobile-app-splash__phone-screen">
              <img src="/apple-touch-icon.png" alt="" className="mobile-app-splash__phone-icon" decoding="async" />
              <BrandWordmark className="brand-wordmark--mobile-app-splash-phone" title="" />
            </div>
          </div>
        </div>
        <div className="mobile-app-splash__chips" aria-hidden="true">
          <span className="mobile-app-splash__chip">{t('vc.mobileAppSplash.chip1')}</span>
          <span className="mobile-app-splash__chip">{t('vc.mobileAppSplash.chip2')}</span>
          <span className="mobile-app-splash__chip">{t('vc.mobileAppSplash.chip3')}</span>
        </div>
        <h1 id="mobile-app-splash-title" className="mobile-app-splash__title">
          {t('vc.mobileAppSplash.title')}
        </h1>
        <a
          href={storeHref}
          className="mobile-app-splash__store"
          target="_blank"
          rel="noopener noreferrer"
        >
          <StoreIcon className="mobile-app-splash__store-icon" />
          <span className="mobile-app-splash__store-copy">
            <span className="mobile-app-splash__store-kicker">{t('vc.headerStoreGet')}</span>
            <span className="mobile-app-splash__store-label">{storeLabel}</span>
          </span>
        </a>
        <button type="button" className="mobile-app-splash__continue" onClick={close}>
          {t('vc.mobileAppSplash.continueWeb')}
        </button>
      </div>
    </div>
  )
}
